export const experimentalDesign = {
  id: "si-1",
  title: "Experimental Design and Setup",
  description: "Understand how experiments are structured and why specific designs are chosen",
  passages: [
    {
      id: "si-1-1",
      title: "Plant Growth Experiment",
      context: "A botanist wants to test whether different light wavelengths affect plant growth. She sets up four identical growth chambers, each containing 20 bean seedlings of the same variety. Chamber A receives red light, Chamber B receives blue light, Chamber C receives green light, and Chamber D receives white light (control). All chambers maintain the same temperature (22°C), humidity (60%), and light intensity (500 lumens). Plants are watered with equal amounts daily and measured weekly for 6 weeks.",
      questions: [
        {
          question: "What is the primary purpose of using Chamber D with white light?",
          options: [
            "To provide the brightest light condition",
            "To serve as a control for comparison with colored light treatments",
            "To test whether white light is better than colored light",
            "To ensure all wavelengths are tested equally"
          ],
          correct: 1,
          explanation: "Chamber D serves as the control group, providing a baseline for comparison. Controls help determine whether the experimental treatments (colored lights) produce effects different from standard conditions."
        },
        {
          question: "Why is it important that all chambers use seedlings from the same plant variety?",
          options: [
            "To reduce genetic variation as a confounding variable",
            "To make the experiment easier to conduct",
            "To ensure the plants grow at the same rate",
            "To test only one type of plant at a time"
          ],
          correct: 0,
          explanation: "Using the same variety controls for genetic differences. If different varieties were used, observed differences might be due to genetics rather than light wavelength, making results unreliable."
        },
        {
          question: "Which aspect of the experimental design ensures that light wavelength is the only variable being tested?",
          options: [
            "Using 20 seedlings per chamber",
            "Measuring plants weekly for 6 weeks",
            "Keeping temperature, humidity, and light intensity constant across all chambers",
            "Using four different chambers"
          ],
          correct: 2,
          explanation: "By keeping all other conditions constant (temperature, humidity, intensity), the experimenter ensures that any differences in growth can be attributed to light wavelength alone, not other environmental factors."
        }
      ]
    },
    {
      id: "si-1-2",
      title: "Antibiotic Effectiveness Study",
      context: "A microbiologist tests three antibiotics (A, B, and C) against bacterial strain X. She prepares 12 identical petri dishes with nutrient agar and spreads equal amounts of bacteria on each. Four dishes receive antibiotic A, four receive antibiotic B, four receive antibiotic C, and four receive no antibiotic (control). All dishes are incubated at 37°C for 24 hours, then the diameter of the bacteria-free zone around each antibiotic disk is measured.",
      questions: [
        {
          question: "Why does the experiment include four dishes for each treatment rather than just one?",
          options: [
            "To use up all available petri dishes",
            "To provide replication and increase reliability of results",
            "To test different bacterial strains",
            "To compare results at different time points"
          ],
          correct: 1,
          explanation: "Replication (multiple trials) is crucial in scientific experiments. It accounts for random variation and increases confidence that results are consistent and not due to chance."
        },
        {
          question: "What is the purpose of the four dishes that receive no antibiotic?",
          options: [
            "To show that bacteria can grow without antibiotics",
            "To serve as a negative control to confirm bacteria are viable",
            "To test whether the agar itself affects bacterial growth",
            "To save antibiotics for other experiments"
          ],
          correct: 1,
          explanation: "The control dishes confirm that bacteria are alive and capable of growing. If bacteria don't grow in control dishes, the experiment is invalid because lack of growth in treatment dishes might be due to non-viable bacteria, not antibiotic effectiveness."
        },
        {
          question: "Which measurement best indicates antibiotic effectiveness in this design?",
          options: [
            "The number of bacterial colonies",
            "The color of the bacterial growth",
            "The diameter of the bacteria-free zone",
            "The time required for bacteria to appear"
          ],
          correct: 2,
          explanation: "The bacteria-free zone (zone of inhibition) directly measures how far the antibiotic diffused while killing bacteria. A larger zone indicates greater effectiveness at stopping bacterial growth."
        }
      ]
    },
    {
      id: "si-1-3",
      title: "Enzyme Temperature Study",
      context: "A biochemist investigates how temperature affects enzyme activity. She prepares five test tubes, each containing the same enzyme concentration and substrate. The tubes are placed in water baths at different temperatures: 10°C, 25°C, 37°C, 50°C, and 65°C. After 5 minutes of temperature equilibration, the reaction is started by adding substrate. The rate of product formation is measured by monitoring color change every 30 seconds for 5 minutes.",
      questions: [
        {
          question: "Why is it important to allow 5 minutes for temperature equilibration before starting the reaction?",
          options: [
            "To give the enzyme time to denature",
            "To ensure the enzyme and substrate reach the target temperature before reacting",
            "To allow the substrate to dissolve completely",
            "To prevent the water bath from cooling down"
          ],
          correct: 1,
          explanation: "Temperature equilibration ensures that the enzyme and substrate are at the desired temperature when the reaction begins. Without this step, initial reaction rates would reflect warming/cooling rather than the effect of the target temperature."
        },
        {
          question: "What is the independent variable in this experiment?",
          options: [
            "Enzyme concentration",
            "Temperature",
            "Rate of product formation",
            "Time of measurement"
          ],
          correct: 1,
          explanation: "The independent variable is what the experimenter deliberately changes—in this case, temperature. The experimenter controls temperature to observe its effect on enzyme activity (the dependent variable)."
        },
        {
          question: "Why does the experimenter measure color change every 30 seconds rather than just at the end?",
          options: [
            "To keep busy during the experiment",
            "To track the reaction rate over time and detect when it slows or stops",
            "To use more data points in calculations",
            "To ensure the color change is visible"
          ],
          correct: 1,
          explanation: "Frequent measurements reveal the reaction's progress over time. Enzyme reactions often slow as substrate is depleted or products accumulate. Measuring only at the end would miss important kinetic information about how quickly the reaction proceeds."
        }
      ]
    },
    {
      id: "si-1-4",
      title: "Soil pH and Plant Nutrient Uptake",
      context: "An agricultural scientist studies how soil pH affects iron uptake in tomato plants. She prepares six groups of tomato seedlings, each group planted in soil adjusted to a different pH: 4.5, 5.5, 6.5, 7.5, 8.5, and 9.5. All plants receive identical amounts of water, fertilizer (containing iron), and sunlight. After 4 weeks, leaves are analyzed for iron content using spectrophotometry.",
      questions: [
        {
          question: "Why does the experiment test a range of pH values rather than just acidic vs. alkaline?",
          options: [
            "To use more plants in the study",
            "To identify the optimal pH for iron uptake and observe trends across the pH spectrum",
            "To make the experiment more complicated",
            "To test whether pH matters at all"
          ],
          correct: 1,
          explanation: "Testing multiple pH values reveals not just whether pH matters, but how iron uptake changes across the pH range. This allows identification of optimal conditions and shows whether the relationship is linear, curved, or has a peak."
        },
        {
          question: "Why is it critical that all plants receive identical amounts of fertilizer containing iron?",
          options: [
            "To ensure iron availability is constant so pH is the only variable affecting uptake",
            "To prevent some plants from growing faster than others",
            "To save money on fertilizer costs",
            "To make watering easier"
          ],
          correct: 0,
          explanation: "If iron amounts varied, differences in leaf iron content could be due to different iron availability rather than pH effects on uptake. Controlling iron input ensures that pH is the variable being tested."
        },
        {
          question: "What is the dependent variable in this experiment?",
          options: [
            "Soil pH",
            "Amount of fertilizer",
            "Iron content in leaves",
            "Number of weeks of growth"
          ],
          correct: 2,
          explanation: "The dependent variable is what is measured as a result of changing the independent variable. Here, iron content in leaves is measured to see how it responds to different soil pH levels."
        }
      ]
    },
    {
      id: "si-1-5",
      title: "Insulation Material Testing",
      context: "An engineer tests the insulating properties of four materials: fiberglass, foam, cellulose, and mineral wool. She constructs four identical boxes (30 cm × 30 cm × 30 cm) with 5 cm thick walls made from each material. A 100-watt heat lamp is placed inside each box, and the internal temperature is monitored. The external temperature is maintained at 20°C. Temperature is recorded every 5 minutes for 1 hour after the lamp is turned on.",
      questions: [
        {
          question: "Why is it important that all boxes have the same dimensions and wall thickness?",
          options: [
            "To make construction easier",
            "To ensure surface area and insulation thickness are constant so material type is the only variable",
            "To fit the boxes in the testing room",
            "To use the same amount of each material"
          ],
          correct: 1,
          explanation: "Different dimensions or thicknesses would affect heat retention independently of material properties. Standardizing these factors ensures that observed differences are due to material insulation properties alone."
        },
        {
          question: "What is the purpose of maintaining the external temperature at 20°C?",
          options: [
            "To keep the room comfortable for the experimenter",
            "To provide a constant temperature gradient for consistent heat loss conditions",
            "To prevent the boxes from overheating",
            "To match typical room temperature"
          ],
          correct: 1,
          explanation: "A constant external temperature ensures that the temperature difference between inside and outside remains consistent across all trials. This standardizes the driving force for heat loss, making material comparisons valid."
        },
        {
          question: "Which material would be considered the best insulator based on this experimental design?",
          options: [
            "The material that reaches the highest internal temperature",
            "The material that heats up most quickly",
            "The material that maintains the most stable temperature",
            "The material with the lowest external surface temperature"
          ],
          correct: 0,
          explanation: "Better insulators prevent heat from escaping, so the box interior stays hotter. The material that achieves and maintains the highest internal temperature is retaining heat most effectively, indicating superior insulation."
        }
      ]
    },
    {
      id: "si-1-6",
      title: "Water Filtration Comparison",
      context: "A student compares three water filtration methods: activated carbon, ceramic filter, and reverse osmosis. She prepares contaminated water containing bacteria, dissolved salts, and organic compounds. Equal volumes (500 mL) of contaminated water are passed through each filtration system at the same flow rate. Filtered water samples are tested for bacterial count, salt concentration (measured by conductivity), and organic compound concentration (measured by UV absorbance).",
      questions: [
        {
          question: "Why does the experiment test three different contaminant types rather than just one?",
          options: [
            "To make the experiment more comprehensive and reveal which filters work best for different contaminants",
            "To use more testing equipment",
            "To make the experiment take longer",
            "To ensure all filters remove something"
          ],
          correct: 0,
          explanation: "Different filtration methods work through different mechanisms. Testing multiple contaminant types reveals each filter's strengths and weaknesses, providing more useful information than testing a single contaminant."
        },
        {
          question: "Why is it important to use the same flow rate for all three filtration systems?",
          options: [
            "To finish the experiment at the same time",
            "To ensure contact time with the filter is constant, making comparisons valid",
            "To prevent the filters from clogging",
            "To use the same pump for all systems"
          ],
          correct: 1,
          explanation: "Flow rate affects contact time between water and filter material. Slower flow allows more time for contaminant removal. Using the same flow rate ensures that differences in filtration effectiveness are due to filter type, not contact time."
        },
        {
          question: "What would be an appropriate control for this experiment?",
          options: [
            "Testing uncontaminated water through all filters",
            "Testing contaminated water with no filtration to establish baseline contamination levels",
            "Using twice as much water for one filter",
            "Testing each filter twice"
          ],
          correct: 1,
          explanation: "A control sample of unfiltered contaminated water establishes the starting contamination levels. This baseline is essential for calculating the percentage of contaminants removed by each filter."
        }
      ]
    },
    {
      id: "si-1-7",
      title: "Photosynthesis Light Intensity Study",
      context: "A biologist investigates how light intensity affects photosynthesis rate in aquatic plants. She places identical sprigs of Elodea (aquatic plant) in test tubes filled with water containing dissolved CO₂. Each tube is positioned at a different distance from a light source: 10 cm, 20 cm, 30 cm, 40 cm, and 50 cm. Light intensity is measured at each distance using a light meter. The rate of photosynthesis is measured by counting oxygen bubbles produced per minute.",
      questions: [
        {
          question: "Why does the experimenter vary distance from the light source rather than using different bulb wattages?",
          options: [
            "Distance is easier to measure than wattage",
            "Changing distance provides a continuous range of light intensities with a single light source",
            "Different bulbs would produce different colors of light",
            "Distance doesn't affect light intensity"
          ],
          correct: 1,
          explanation: "Light intensity decreases predictably with distance (inverse square law). Using one light source at different distances provides a range of intensities while controlling for light quality (color spectrum), which could vary between different bulbs."
        },
        {
          question: "Why is counting oxygen bubbles a valid measure of photosynthesis rate?",
          options: [
            "Oxygen bubbles are easy to see",
            "Oxygen is a direct product of photosynthesis, so bubble rate indicates reaction rate",
            "Bubbles indicate the plant is healthy",
            "More bubbles mean more CO₂ is being used"
          ],
          correct: 1,
          explanation: "Photosynthesis produces oxygen as a byproduct (6CO₂ + 6H₂O → C₆H₁₂O₆ + 6O₂). The rate of oxygen production directly reflects the rate of photosynthesis, making bubble counting a simple but effective measurement method."
        },
        {
          question: "What is a potential limitation of this experimental design?",
          options: [
            "Light intensity is the only factor being tested",
            "Bubble counting may be subjective and small bubbles might be missed",
            "Too many distances are being tested",
            "Elodea is not a good plant for photosynthesis studies"
          ],
          correct: 1,
          explanation: "Counting bubbles by eye can be imprecise—small bubbles may be missed, and rapid bubbling is hard to count accurately. A more precise method would be collecting and measuring oxygen volume, though bubble counting is acceptable for demonstrating trends."
        }
      ]
    },
    {
      id: "si-1-8",
      title: "Rust Formation Conditions",
      context: "A chemist studies factors affecting iron rust formation. She prepares 12 identical iron nails and divides them into four groups of three. Group A nails are submerged in water with air exposure. Group B nails are submerged in boiled water (oxygen-depleted) sealed from air. Group C nails are exposed to humid air but not submerged. Group D nails are kept in dry air with desiccant. All groups are maintained at 25°C for 2 weeks, then rust coverage is assessed.",
      questions: [
        {
          question: "What is the purpose of using boiled water sealed from air in Group B?",
          options: [
            "To test whether hot water causes more rust",
            "To remove dissolved oxygen and test whether oxygen is necessary for rust formation",
            "To sterilize the water",
            "To make the water evaporate faster"
          ],
          correct: 1,
          explanation: "Boiling removes dissolved oxygen from water, and sealing prevents oxygen from re-entering. This tests whether oxygen is required for rust formation. If Group B shows no rust while Group A does, oxygen is confirmed as necessary."
        },
        {
          question: "Why does the experiment include both Group C (humid air) and Group D (dry air)?",
          options: [
            "To test whether water/moisture is necessary for rust formation",
            "To use all available nails",
            "To test different types of air",
            "To see which condition is more common"
          ],
          correct: 0,
          explanation: "Comparing humid vs. dry air tests whether moisture is required for rusting. If Group C rusts but Group D doesn't, moisture is confirmed as necessary. This complements the water submersion tests in Groups A and B."
        },
        {
          question: "Based on the experimental design, what conclusion could be drawn if only Group A shows significant rust?",
          options: [
            "Iron only rusts underwater",
            "Both water and oxygen are necessary for rust formation",
            "Temperature is the most important factor",
            "Rust forms randomly"
          ],
          correct: 1,
          explanation: "Group A has both water and oxygen. Group B lacks oxygen, Group C lacks full water submersion, and Group D lacks moisture. If only Group A rusts significantly, it suggests both water and oxygen are required together for rust formation."
        }
      ]
    },
    {
      id: "si-1-9",
      title: "Seed Germination Requirements",
      context: "A botanist tests what conditions are necessary for bean seed germination. She sets up six groups of 20 seeds each under different conditions: Group 1 (water + light + warmth at 25°C), Group 2 (water + light + cold at 5°C), Group 3 (water + darkness + warmth), Group 4 (no water + light + warmth), Group 5 (water only, no light or warmth at 15°C), and Group 6 (no water + darkness + cold). Seeds are checked daily for 10 days and germination percentage is recorded.",
      questions: [
        {
          question: "Which group serves as the positive control showing optimal germination conditions?",
          options: [
            "Group 6, because it tests the most extreme conditions",
            "Group 1, because it provides all favorable conditions",
            "Group 4, because it tests the importance of water",
            "Group 5, because it has water"
          ],
          correct: 1,
          explanation: "Group 1 provides all conditions typically favorable for germination (water, light, warmth). This positive control should show high germination, confirming that the seeds are viable and the experimental setup works."
        },
        {
          question: "What is the purpose of Group 4 (no water + light + warmth)?",
          options: [
            "To test whether light and warmth alone can cause germination",
            "To demonstrate that water is essential for germination",
            "To save water during the experiment",
            "To test whether seeds can survive without water"
          ],
          correct: 1,
          explanation: "Group 4 isolates water as a variable. By providing light and warmth but withholding water, the experimenter can determine whether water is essential. If Group 4 shows no germination while Group 1 does, water is confirmed as necessary."
        },
        {
          question: "Why does the experiment use 20 seeds per group rather than just one or two?",
          options: [
            "To use up all available seeds",
            "To provide statistical reliability and account for natural variation in seed viability",
            "To make the experiment take longer",
            "To test different types of seeds"
          ],
          correct: 1,
          explanation: "Individual seeds vary in viability and germination potential. Using 20 seeds per group provides a sample size large enough to calculate meaningful germination percentages and reduces the impact of random variation."
        }
      ]
    },
    {
      id: "si-1-10",
      title: "Battery Performance Testing",
      context: "An engineer compares the performance of four battery types (alkaline, lithium, rechargeable NiMH, and zinc-carbon) under different conditions. She tests each battery type in three scenarios: continuous low drain (LED flashlight), intermittent medium drain (digital camera), and continuous high drain (motorized toy). For each combination, three batteries are tested and runtime until voltage drops below 1.0V is recorded.",
      questions: [
        {
          question: "Why does the experiment test batteries under three different drain conditions?",
          options: [
            "To use more batteries",
            "To reveal which battery types perform best under different usage patterns",
            "To make the experiment more complicated",
            "To test whether drain rate matters"
          ],
          correct: 1,
          explanation: "Different battery chemistries perform differently under various drain rates. Some excel in low-drain applications, others in high-drain. Testing multiple conditions provides practical information about which battery is best for specific uses."
        },
        {
          question: "Why is voltage drop below 1.0V used as the endpoint rather than complete battery failure?",
          options: [
            "1.0V is easier to measure",
            "Most devices stop functioning properly below 1.0V, making this a practical endpoint",
            "Batteries explode below 1.0V",
            "1.0V means the battery is completely empty"
          ],
          correct: 1,
          explanation: "Most electronic devices require a minimum voltage to operate. Using 1.0V as the endpoint reflects real-world battery life—when devices stop working—rather than waiting for complete chemical depletion, which may take much longer."
        },
        {
          question: "What is the purpose of testing three batteries of each type in each condition?",
          options: [
            "To ensure results are reproducible and not due to manufacturing defects in individual batteries",
            "To use up all available batteries",
            "To test batteries from different packages",
            "To make the experiment take three times longer"
          ],
          correct: 0,
          explanation: "Replication with three batteries accounts for manufacturing variation and random defects. If all three batteries of a type perform similarly, the results are reliable. If one performs very differently, it may be defective rather than representative."
        }
      ]
    }
  ]
};